import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { ReactPortalElementId } from 'core/layouts';
import { Table } from 'ui/table';
import { ColumnData } from 'ui/tableHeader';
import { TableRows } from 'ui/tableRows';

import { StyledPaginationFooter, UsersTableWrapper, ScrollWrapper } from './UsersTable.styles';
import { UsersTableHeader } from './UsersTableHeader';
import { UsersTableActions } from './UsersTableActions';
import { NoResultsEmptyState } from '../NoResultsEmptyState';
import { ResultsTableSkeleton } from '../ResultsTableSkeleton';
import { useGetColumnData, useIsUuidProject, useGetUserFieldDisplayName } from '../../../hooks';
import { STARRED_FIELDS } from '../../../constants';
import { useSegmentationQueryContext } from '../../../contexts/segmentationQuery';
import { useUsersTableContext } from '../../../contexts/usersTable';
import { useUsersTableColumnsContext } from '../../../contexts/usersTableColumns';
import { useGetUserFieldType } from '../../../hooks/useGetUserFieldType';
import { isDefined } from 'utils/tsHelpers';
import { useMemo } from 'react';

const StyledTableRows = styled(TableRows)`
  word-break: break-all;
  word-break: break-word; /* deprecated, falls back to break-all if unsupported */
`;

export const testIds = {
  table: 'segmentation-users-table',
};

export const UsersTable = () => {
  const { results, count, isLoading } = useSegmentationQueryContext();
  const { getFieldDisplayName } = useGetUserFieldDisplayName();
  const isEnabled = isDefined(count) && count > 0;

  const { getFieldTypeFromField, isLoading: isMappingsLoading } = useGetUserFieldType(isEnabled);
  const { currentPage, pageSize, setCurrentPage, setPageSize } = useUsersTableContext();
  const { selectedColumns } = useUsersTableColumnsContext();
  const { refreshQueryResults } = useSegmentationQueryContext();
  const portalPath = document.getElementById(
    ReactPortalElementId.sementationInsightsToolbarActions,
  );

  const memoizedSelectedColumns = useMemo(() => {
    return selectedColumns.map((column) => ({
      field: column,
      title: getFieldDisplayName(column),
      fieldType: getFieldTypeFromField(column),
    }));
  }, [selectedColumns]);

  const isUuidProject = useIsUuidProject();

  const columns: ColumnData[] = useGetColumnData(memoizedSelectedColumns);

  const hasResults = !!results?.length && !isLoading && !isMappingsLoading;

  const getUsersTableContent = () =>
    hasResults ? (
      <>
        <ScrollWrapper>
          <Table>
            <UsersTableHeader columns={columns} />
            <StyledTableRows
              rows={results}
              idKey={isUuidProject ? STARRED_FIELDS.userId : STARRED_FIELDS.email}
              columns={columns}
            />
          </Table>
        </ScrollWrapper>
        <StyledPaginationFooter
          currentPage={currentPage}
          itemsPerPage={pageSize}
          numItems={count}
          onUpdate={({ page, pageSize: newPageSize }) => {
            setCurrentPage(page);
            setPageSize(newPageSize);
            refreshQueryResults({ page, pageSize: newPageSize });
          }}
        />
      </>
    ) : (
      <NoResultsEmptyState />
    );

  return (
    <UsersTableWrapper data-test={testIds.table}>
      {portalPath && createPortal(<UsersTableActions />, portalPath)}
      {isLoading ? <ResultsTableSkeleton /> : getUsersTableContent()}
    </UsersTableWrapper>
  );
};
